import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ColorPicker from './ColorPicker';
import { getFlyerPromptBuilder } from './flyerPrompts';
import { generateAndUploadFlyer } from './geminiService';
import {
  API_GROUPS,
  BUCKET_CS_IMAGES_UPLOAD,
  S3_GET_PRESIGNED_URLS
} from '../../../utils/constants';
import './FlyerModal.css';

const FlyerModal = ({ onClose, onBack, onFlyerGenerated, props }) => {
  const userInfo = useSelector((state) => state.auth.userInfo);
  const currentClient = useSelector((state) => state.selectedClient);

  const [selectedFlyerType, setSelectedFlyerType] = useState(null);
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [industryName, setIndustryName] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  // Client gallery states
  const [clientImages, setClientImages] = useState([]);
  const [selectedImageFromGallery, setSelectedImageFromGallery] =
    useState(null);

  // Random flyer type selection
  const getRandomFlyerType = () => {
    const types = ['type1', 'type2', 'type3', 'type4', 'type5'];
    return types[Math.floor(Math.random() * types.length)];
  };

  // Default colors per flyer type
  const getDefaultColors = (flyerType) => {
    const defaults = {
      type1: { primary: '#FF6B6B', secondary: '#4ECDC4' },
      type2: { primary: '#A8E6CF', secondary: '#FFD93D' },
      type3: { primary: '#FF8B94', secondary: '#B4A7D6' },
      type4: { primary: '#D4A574', secondary: '#85C1E9' },
      type5: { primary: '#F39C12', secondary: '#E74C3C' }
    };
    return defaults[flyerType] || { primary: '#3B5998', secondary: '#8392A5' };
  };

  // Get colors: client brand colors if available, otherwise defaults based on flyer type
  const getCurrentColors = () => {
    if (
      props.currentClientBrand?.primaryColor &&
      props.currentClientBrand?.secondaryColor
    ) {
      return {
        primary: props.currentClientBrand.primaryColor,
        secondary: props.currentClientBrand.secondaryColor
      };
    }
    return getDefaultColors(selectedFlyerType);
  };

  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');

  // Initialize random flyer type on mount
  useEffect(() => {
    if (!selectedFlyerType) {
      setSelectedFlyerType(getRandomFlyerType());
    }
  }, []);

  // Update colors when flyer type changes
  React.useEffect(() => {
    const colors = getCurrentColors();
    setPrimaryColor(colors.primary);
    setSecondaryColor(colors.secondary);
  }, [selectedFlyerType, props.currentClientBrand]);

  // Load client images from posts and client library
  useEffect(() => {
    const images = [];

    // Get images from client posts
    if (props?.postsLists && Array.isArray(props.postsLists)) {
      props.postsLists.forEach((post) => {
        if (post.images && post.images.length > 0) {
          post.images.forEach((img) => {
            const url = img.url || img.originalURL;
            if (url) {
              images.push({ url, source: 'posts' });
            }
          });
        }
      });
    }

    // Get images from client library
    const clientLibraryImages = currentClient?.selectedClient?.images || [];
    clientLibraryImages.forEach((img) => {
      const url = img.originalURL || img.url;
      if (url) {
        images.push({ url, source: 'library' });
      }
    });

    setClientImages(images);
  }, [props.postsLists, currentClient?.selectedClient?.images]);

  // Get client name from current client state
  const getClientName = () => {
    return currentClient?.selectedClient?.clientName;
  };

  // Get random image from client gallery
  const getRandomImageFromGallery = () => {
    if (clientImages.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * clientImages.length);
    return clientImages[randomIndex].url;
  };

  // Get industry name from industry ID
  useEffect(() => {
    const fetchIndustryName = async () => {
      if (!currentClient?.selectedClient?.industry || !userInfo?.accessToken) {
        setIndustryName('Business');
        return;
      }

      try {
        const response = await axios.get(API_GROUPS, {
          headers: { Authorization: `bearer ${userInfo.accessToken}` }
        });
        const groups = response.data.groups;
        const industry = groups?.find(
          (g) => g.id === currentClient?.selectedClient?.industry
        );
        setIndustryName(industry?.groupName || 'Business');
      } catch (error) {
        console.error('Error fetching industry:', error);
        setIndustryName('Business');
      }
    };

    fetchIndustryName();
  }, [currentClient?.industry, userInfo?.accessToken]);

  const handleClose = () => {
    onClose();
  };

  // Handle image selection from gallery
  const handleImageSelect = (imageUrl) => {
    setSelectedImageFromGallery(imageUrl);
  };

  // Handle regenerate flyer with new random template and image
  const handleRegenerateFlyer = async () => {
    // Select new random flyer type
    const newFlyerType = getRandomFlyerType();
    setSelectedFlyerType(newFlyerType);

    // Clear current generated image
    setGeneratedImageUrl(null);

    // Clear selected image to force a new random one
    setSelectedImageFromGallery(null);

    // Update colors for the new flyer type
    const newColors = getDefaultColors(newFlyerType);
    setPrimaryColor(newColors.primary);
    setSecondaryColor(newColors.secondary);

    // Generate new flyer with new random image
    await handleGenerateFlyer(newFlyerType, true);
  };

  const handleGenerateFlyer = async (
    flyerType = null,
    forceRandomImage = false
  ) => {
    // Validation
    if (!description.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Description Required',
        text: 'Please enter a description for your flyer'
      });
      return;
    }

    // Select a new random flyer type if not provided
    const typeToUse = flyerType || getRandomFlyerType();

    // Update the selected flyer type state
    setSelectedFlyerType(typeToUse);

    // Update colors for the selected flyer type
    const newColors = getDefaultColors(typeToUse);
    setPrimaryColor(newColors.primary);
    setSecondaryColor(newColors.secondary);

    setIsGenerating(true);

    try {
      // Get merge fields for phone and website
      const mergeFields = currentClient?.selectedClient?.mergeFields || {};
      const phone = mergeFields['~Phone'] || '';
      const website = mergeFields['~Website'] || '';

      // Handle reference image: use random if forced, otherwise use selected or random
      let referenceImageUrl;
      if (forceRandomImage) {
        referenceImageUrl = getRandomImageFromGallery();
      } else {
        referenceImageUrl =
          selectedImageFromGallery || getRandomImageFromGallery();
      }

      // Build prompt data
      const promptData = {
        INDUSTRY: industryName,
        TITLE: getClientName(),
        DESCRIPTION: description.trim(),
        DETAILS: details.trim(),
        PHONE: phone,
        WEBSITE: website,
        PRIMARY_COLOR: primaryColor,
        SECONDARY_COLOR: secondaryColor
      };

      // Get the appropriate prompt builder for the selected flyer type
      const promptBuilder = getFlyerPromptBuilder(typeToUse);
      const prompt = promptBuilder(promptData);

      // Generate and upload flyer (Lambda handles both)
      const { url } = await generateAndUploadFlyer(
        prompt,
        currentClient?.id,
        referenceImageUrl
      );

      setGeneratedImageUrl(url);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to generate flyer. Please try again.'
      });
      console.error('Flyer generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1060,
        overflowY: 'auto'
      }}>
      <div
        className="modal-dialog"
        style={{ marginTop: '3vh', marginBottom: '3vh', maxWidth: '600px' }}>
        <div
          className="modal-content"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5em',
            width: '100%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            color: '#677793',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5em'
            }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <button
                type="button"
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#677793'
                }}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <h5 style={{ margin: 0, fontWeight: '500' }}>Generate Flyer</h5>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#677793'
              }}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Generated Flyer Preview */}
          {generatedImageUrl && (
            <div style={{ marginBottom: '1.5em' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: '#677793',
                  marginBottom: '0.5em',
                  fontWeight: '500'
                }}>
                Generated Flyer Preview
              </label>
              <div
                style={{
                  padding: '1em',
                  border: '2px solid #3766a8',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9',
                  textAlign: 'center'
                }}>
                <img
                  src={generatedImageUrl}
                  alt="Generated Flyer"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    borderRadius: '8px',
                    marginBottom: '1em'
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    gap: '1em',
                    justifyContent: 'center',
                    marginTop: '1em'
                  }}>
                  <button
                    onClick={handleRegenerateFlyer}
                    disabled={isGenerating}
                    style={{
                      padding: '0.75em 1.5em',
                      border: '1px solid #3766a8',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#3766a8',
                      cursor: isGenerating ? 'not-allowed' : 'pointer',
                      opacity: isGenerating ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5em'
                    }}>
                    {isGenerating && <i className="fas fa-spinner fa-spin"></i>}
                    <i className="fas fa-sync-alt"></i>
                    Re-generate Flyer
                  </button>
                  <button
                    onClick={() => {
                      onFlyerGenerated(generatedImageUrl);
                      handleClose();
                    }}
                    disabled={isGenerating}
                    style={{
                      padding: '0.75em 1.5em',
                      border: 'none',
                      borderRadius: '8px',
                      background: '#3766a8',
                      color: 'white',
                      cursor: isGenerating ? 'not-allowed' : 'pointer',
                      opacity: isGenerating ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5em'
                    }}>
                    <i className="fas fa-check"></i>
                    Use This Flyer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div style={{ marginBottom: '1em' }}>
            <label
              htmlFor="flyerDescription"
              style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#677793',
                marginBottom: '0.3em'
              }}>
              Description *
            </label>
            <textarea
              id="flyerDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter flyer description"
              rows={3}
              maxLength={500}
              style={{
                width: '100%',
                padding: '0.8em',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Details (Optional) */}
          <div style={{ marginBottom: '1em' }}>
            <label
              htmlFor="flyerDetails"
              style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#677793',
                marginBottom: '0.3em'
              }}>
              Details (Optional)
            </label>
            <textarea
              id="flyerDetails"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Enter additional details"
              rows={2}
              maxLength={300}
              style={{
                width: '100%',
                padding: '0.8em',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Client Image Gallery */}
          {clientImages.length > 0 && (
            <div style={{ marginBottom: '1.5em' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: '#677793',
                  marginBottom: '0.5em',
                  fontWeight: '500'
                }}>
                Select Image from Gallery (Optional)
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '0.5em',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '0.5em',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                }}>
                {clientImages.map((image, index) => (
                  <div
                    key={index}
                    onClick={() => handleImageSelect(image.url)}
                    style={{
                      position: 'relative',
                      cursor: 'pointer',
                      border:
                        selectedImageFromGallery === image.url
                          ? '3px solid #3766a8'
                          : '2px solid transparent',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      aspectRatio: '1',
                      transition: 'all 0.2s'
                    }}>
                    <img
                      src={image.url}
                      alt={`Client ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {selectedImageFromGallery === image.url && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: '#3766a8',
                          color: 'white',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem'
                        }}>
                        ✓
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: '0.5em',
                  fontSize: '0.8rem',
                  color: '#666'
                }}>
                {selectedImageFromGallery
                  ? 'Selected image will be used in the flyer'
                  : 'No image selected. A random image will be used from your gallery'}
              </div>
            </div>
          )}

          {/* Logo Display */}
          {props.currentClientLogo && (
            <div style={{ marginBottom: '1.5em' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: '#677793',
                  marginBottom: '0.5em',
                  fontWeight: '500'
                }}>
                Logo
              </label>
              <div
                style={{
                  padding: '1em',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5em',
                  backgroundColor: '#f9f9f9'
                }}>
                <img
                  src={props.currentClientLogo}
                  alt="Client Logo"
                  style={{
                    maxWidth: '80px',
                    maxHeight: '80px',
                    objectFit: 'contain'
                  }}
                />
                <span style={{ fontSize: '0.85rem', color: '#666' }}>
                  Client logo will be included in the flyer
                </span>
              </div>
            </div>
          )}

          {/* Advanced Options */}
          <div style={{ marginBottom: '1.5em' }}>
            <div
              style={{
                cursor: 'pointer',
                padding: '0.8em',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: showAdvancedOptions ? '#f0f7ff' : '#f9f9f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s'
              }}
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
              <span
                style={{
                  fontSize: '0.9rem',
                  color: '#677793',
                  fontWeight: '500'
                }}>
                Advanced Options
              </span>
              <span style={{ fontSize: '1.2rem', color: '#677793' }}>
                {showAdvancedOptions ? '−' : '+'}
              </span>
            </div>

            {showAdvancedOptions && (
              <div
                style={{
                  marginTop: '1em',
                  padding: '1em',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa'
                }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: '#677793',
                    marginBottom: '0.5em',
                    fontWeight: '500'
                  }}>
                  Brand Colors
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1em'
                  }}>
                  <ColorPicker
                    label="Primary Color"
                    color={primaryColor}
                    onChange={setPrimaryColor}
                  />
                  <ColorPicker
                    label="Secondary Color"
                    color={secondaryColor}
                    onChange={setSecondaryColor}
                  />
                </div>
                <div
                  style={{
                    marginTop: '0.5em',
                    fontSize: '0.8rem',
                    color: '#666'
                  }}>
                  Colors will be automatically selected based on the flyer
                  design if not customized
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1em',
              marginTop: '1.5em',
              paddingTop: '1em',
              borderTop: '1px solid #e0e0e0'
            }}>
            <button
              onClick={onClose}
              disabled={isGenerating}
              style={{
                padding: '0.75em 1.5em',
                border: '1px solid #ccc',
                borderRadius: '8px',
                background: 'white',
                color: '#677793',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                opacity: isGenerating ? 0.6 : 1
              }}>
              Cancel
            </button>
            <button
              onClick={handleGenerateFlyer}
              disabled={isGenerating}
              style={{
                padding: '0.75em 1.5em',
                border: 'none',
                borderRadius: '8px',
                background: isGenerating ? '#999' : '#3766a8',
                color: 'white',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5em'
              }}>
              {isGenerating && <i className="fas fa-spinner fa-spin"></i>}
              {isGenerating ? 'Generating...' : 'Generate Flyer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlyerModal;
